import type { Language } from '@/types';

interface Nav {
  home: string;
  howItWorks: string;
  claim: string;
  faq: string;
  about: string;
  contact: string;
}

interface Hero {
  title: string;
  subtitle: string;
  ctaPrimary: string;
  ctaSecondary: string;
  trust1: string;
  trust2: string;
  trust3: string;
}

interface HowItWorks {
  title: string;
  step1Title: string;
  step1Text: string;
  step2Title: string;
  step2Text: string;
  step3Title: string;
  step3Text: string;
}

interface WhyDelay24 {
  title: string;
  reason1Title: string;
  reason1Text: string;
  reason2Title: string;
  reason2Text: string;
  reason3Title: string;
  reason3Text: string;
  reason4Title: string;
  reason4Text: string;
}

interface Eligibility {
  title: string;
  bullet1: string;
  bullet2: string;
  bullet3: string;
  bullet4: string;
  bullet5: string;
  note: string;
}

interface MiniFaq {
  title: string;
  q1: string;
  a1: string;
  q2: string;
  a2: string;
  q3: string;
  a3: string;
}

interface Footer {
  menu: string;
  legal: string;
  terms: string;
  privacy: string;
  kvkk: string;
  priceList: string;
  description: string;
  rights: string;
}

interface ClaimForm {
  title: string;
  subtitle: string;
  step1: string;
  step2: string;
  step3: string;
  step4: string;
  departureAirport: string;
  arrivalAirport: string;
  flightNumber: string;
  flightDate: string;
  selectDate: string;
  flightDateFuture: string;
  flightDateTooOld: string;
  flightDateInvalid: string;
  flightSegmentLabel: string;
  addConnectingFlight: string;
  removeFlight: string;
  singleBookingQuestion: string;
  singleBookingYes: string;
  singleBookingNo: string;
  selfTransferStopTitle: string;
  selfTransferStopBody: string;
  segmentNotFoundTitle: string;
  segmentNotFoundBody: string;
  segmentAmbiguousTitle: string;
  segmentAmbiguousBody: string;
  problemType: string;
  delayed: string;
  cancelled: string;
  denied: string;
  missed: string;
  delayLength: string;
  delayLengthNote: string;
  delay0to2: string;
  delay2to3: string;
  delay3to4: string;
  delay4plus: string;
  alreadyClaimed: string;
  alreadyClaimedYes: string;
  alreadyClaimedNo: string;
  alreadyClaimedNoteLabel: string;
  alreadyClaimedNotePlaceholder: string;
  alreadyClaimedRequired: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  dateOfBirthFuture: string;
  dateOfBirthInvalid: string;
  email: string;
  phone: string;
  phoneCountryCode: string;
  phoneInvalid: string;
  phoneTooLong: string;
  airline: string;
  bookingRef: string;
  bookingRefHint: string;
  bookingRefInvalid: string;
  bookingRefTooLong: string;
  /** The Booking reference as `sameBookingHint` names it, once it is usable. Takes `{ref}`. */
  bookingRefNamed: string;
  /** Stands in for {@link bookingRefNamed} while no usable Booking reference has been given. */
  bookingRefFallback: string;
  passengerListHeading: string;
  leadPassengerLabel: string;
  passengerLabel: string;
  addPassenger: string;
  /**
   * Takes `{booking}` — the whole noun phrase, not the bare code, because Turkish suffixes the noun
   * differently with and without one ('ABC123 rezervasyonundaki' vs 'bu rezervasyondaki').
   */
  sameBookingHint: string;
  removePassenger: string;
  optionalLabel: string;
  underEighteenQuestion: string;
  underEighteenYes: string;
  underEighteenNo: string;
  leadNotAdult: string;
  minorIsAdult: string;
  duplicateAdultEmail: string;
  coAdultSigningHint: string;
  coAdultNameFallback: string;
  sameAddressAsLead: string;
  addressOfDomicile: string;
  addressOfDomicileHint: string;
  street: string;
  streetPlaceholder: string;
  postalCode: string;
  city: string;
  addressCountry: string;
  addressTooLong: string;
  signatureTitle: string;
  signatureDescription: string;
  signaturePlaceholder: string;
  clearSignature: string;
  signatureConsentPre: string;
  signatureConsentLink: string;
  signatureConsentPost: string;
  authorityDocumentMissingFields: string;
  authorityDocumentDownloadFailed: string;
  signatureRequired: string;
  ticket: string;
  boardingPass: string;
  airlineDocs: string;
  consentAll: string;
  termsAndConditionsConsentPre: string;
  termsAndConditionsConsentLink: string;
  termsAndConditionsConsentPost: string;
  privacyPolicyConsentPre: string;
  privacyPolicyConsentLink: string;
  privacyPolicyConsentPost: string;
  priceListConsentPre: string;
  priceListConsentLink: string;
  priceListConsentPost: string;
  next: string;
  back: string;
  submit: string;
  checking: string;
  ineligibleTitle: string;
  ineligibleMessage: string;
  notPursuableTitle: string;
  notPursuableMessage: string;
  successTitle: string;
  successMessage: string;
  successInfo: string;
  successCoTravellerInfo: string;
  backToHome: string;
  fillAllFields: string;
  invalidEmail: string;
  checkFailed: string;
  submitFailed: string;
  travelDocLabel: string;
  identityDocLabel: string;
  clickToSelect: string;
  removeFile: string;
  docsNote: string;
  selectPlaceholder: string;
  fieldRequired: string;
  stepConfirm: string;
  legLabel: string;
  statusDelayed: string;
  statusCanceled: string;
  totalDelay: string;
  flightDistanceLabel: string;
  scheduledArrival: string;
  actualArrival: string;
  notArrived: string;
  estimateLabel: string;
  perPerson: string;
  estimateNote: string;
  isInfoCorrect: string;
  confirmYes: string;
  confirmNo: string;
  confirmRequired: string;
  hours: string;
  minutes: string;
  fileNotAdded: string;
  fileTooLarge: string;
  invalidFileType: string;
}

interface Home {
  heroTitle1: string;
  heroTitle2Pre: string;
  heroTitle2Highlight: string;
  heroTitle2Post: string;
  heroSubtitle: string;
  flightInputPlaceholder: string;
  checkButton: string;
  quickCheckTerms: string;
  whySubtitle: string;
  ctaTitle: string;
  ctaSubtitle: string;
  ctaBullet1: string;
  ctaBullet2: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

interface Faq {
  title: string;
  subtitle: string;
  contactTitle: string;
  contactText: string;
  contactLink: string;
  items: FaqItem[];
}

interface Contact {
  title: string;
  subtitle: string;
  emailLabel: string;
  addressLabel: string;
  formTitle: string;
  nameField: string;
  emailField: string;
  subjectField: string;
  messageField: string;
  sendButton: string;
  sending: string;
  successToast: string;
  /** Names support@delay24.com by hand, like the addresses on the page; update it if the backend's contact address changes. */
  errorToast: string;
}

interface Autocomplete {
  searching: string;
  error: string;
  noMatches: string;
  select: string;
}

interface NotFound {
  heading: string;
  body: string;
  button: string;
}

interface SigningPage {
  loading: string;
  title: string;
  greeting: string;
  addedBy: string;
  bookingRef: string;
  wrongBooking: string;
  wrongBookingLink: string;
  flightsTitle: string;
  legLabel: string;
  airline: string;
  flightNumber: string;
  flightDate: string;
  notFoundTitle: string;
  notFoundMessage: string;
  notFoundNewestEmail: string;
  usedTitle: string;
  usedMessage: string;
  expiredTitle: string;
  expiredMessage: string;
  resend: string;
  resending: string;
  resendFailed: string;
  resentTitle: string;
  resentMessage: string;
  errorTitle: string;
  errorMessage: string;
  retry: string;
  detailsTitle: string;
  detailsHint: string;
  documentsTitle: string;
  documentsHint: string;
  signatureDescription: string;
  previewNeedsAddress: string;
  sign: string;
  signing: string;
  signFailed: string;
  signedTitle: string;
  signedMessage: string;
  underAgeTitle: string;
  underAgeMessage: string;
  underAgeCorrect: string;
}

interface Translations {
  nav: Nav;
  hero: Hero;
  howItWorks: HowItWorks;
  whyDelay24: WhyDelay24;
  eligibility: Eligibility;
  miniFaq: MiniFaq;
  footer: Footer;
  claimForm: ClaimForm;
  home: Home;
  faq: Faq;
  contact: Contact;
  autocomplete: Autocomplete;
  notFound: NotFound;
  signingPage: SigningPage;
}

export const translations: Record<Language, Translations> = {
  tr: {
    nav: {
      home: 'Ana Sayfa',
      howItWorks: 'Nasıl Çalışır',
      claim: 'Tazminat Talep Et',
      faq: 'SSS',
      about: 'Hakkımızda',
      contact: 'İletişim'
    },
    hero: {
      title: 'Uçuşun Geciktiyse, Tazminat Hakkın Var.',
      subtitle: 'Delay24, geciken, iptal olan veya overbook edilen uçuşların için havayollarından senin adına tazminat talep eder. Sen hiçbir şeyle uğraşma, biz tüm süreci yönetelim. Kazanırsak komisyon alırız, kazanamazsak ücret yok.',
      ctaPrimary: 'Tazminatını Hemen Kontrol Et',
      ctaSecondary: 'Nasıl Çalıştığımızı Gör',
      trust1: 'Ücretsiz ön kontrol',
      trust2: 'Kazanırsak komisyon',
      trust3: 'Avrupa ve Türkiye mevzuatına uygun'
    },
    howItWorks: {
      title: '3 Adımda Tazminat Sürecin',
      step1Title: '1. Uçuş Bilgilerini Gir',
      step1Text: 'Kalkış ve varış havalimanını, uçuş numaranı ve tarihini gir. Sistemimiz, EU261 ve ilgili mevzuata göre tazminat hakkın olup olmadığını otomatik analiz eder.',
      step2Title: '2. Biz Havayoluyla İlgilenelim',
      step2Text: 'Gerekli tüm evrakları topluyor, havayoluna resmi başvurunu yapıyoruz. Gerektiğinde işbirliği yaptığımız avukatlar ile hukuki süreci senin adına takip ediyoruz.',
      step3Title: '3. Tazminat Ödensin, Paylaşalım',
      step3Text: 'Havayolundan tazminat tahsil edildiğinde, anlaştığımız komisyonu aldıktan sonra kalan tutarı sana ödüyoruz. Kazanamazsak hiçbir ücret ödemezsin.'
    },
    whyDelay24: {
      title: 'Neden Delay24?',
      reason1Title: '₺0 Ön Ödeme',
      reason1Text: 'Başvuru sırasında hiçbir ücret ödemezsin. Tüm masrafları biz üstlenir, sadece kazandığımızda komisyon alırız.',
      reason2Title: 'Havayollarına Karşı Profesyonel Takip',
      reason2Text: 'Havayolları ilk başvuruları sıklıkla reddedebilir. Biz hukuki argümanlarla süreci sürdürüyor, hak ettiğin tazminatı almak için sonuna kadar mücadele ediyoruz.',
      reason3Title: 'Türkiye + Avrupa Odaklı',
      reason3Text: 'Hem AB uçuşlarında (EU261) hem de Türkiye çıkışlı/varışlı uçuşlarda (SHY) haklarını takip ediyoruz.',
      reason4Title: 'Şeffaf Dijital Süreç',
      reason4Text: 'Başvurunun hangi aşamada olduğunu online panelden görebilirsin. Ne zaman hangi adımı attığımızı şeffaf biçimde takip edersin.'
    },
    eligibility: {
      title: 'Tazminat Hakkın Olabilir Eğer…',
      bullet1: 'Uçuşun 3 saatten fazla geciktiyse',
      bullet2: 'Uçuşun iptal edildiyse',
      bullet3: 'Overbook nedeniyle uçağa alınmadıysan',
      bullet4: 'Bağlantılı uçuşunu, önceki uçuşun gecikmesi yüzünden kaçırdıysan',
      bullet5: 'Uçuşun AB ülkeleriyle bağlantılı ise (AB\'den kalkış, AB\'ye varış veya AB hava yolu)',
      note: 'Ağır hava koşulları, havaalanı/ATC grevleri veya ciddi güvenlik riskleri gibi olağanüstü durumlarda tazminat hakkı doğmayabilir. Her vakayı ayrı ayrı inceliyoruz.'
    },
    miniFaq: {
      title: 'Sık Sorulan Kısa Sorular',
      q1: 'Ücreti ne zaman ödüyorum?',
      a1: 'Sadece tazminat kazandığımızda, havayolundan gelen tutar üzerinden komisyon alıyoruz. Kazanamazsak hiçbir ücret ödemezsin.',
      q2: 'Ne kadar tazminat alabilirim?',
      a2: 'AB uçuşlarında tazminat tutarı genellikle 250€ – 600€ arasında değişir. Uçuş mesafesi, gecikme süresi ve rota detaylarına göre sistemimiz sana bir tahmin gösterir.',
      q3: 'Süreç ne kadar sürüyor?',
      a3: 'Havayolunun yanıt süresine göre değişse de, ortalama 4–16 hafta arasında sonuç alınır. Dava aşamasına taşınan dosyalarda bu süre uzayabilir.'
    },
    footer: {
      menu: 'Menü',
      legal: 'Hukuki',
      terms: 'Kullanım Şartları',
      privacy: 'Gizlilik Politikası',
      kvkk: 'KVKK',
      priceList: 'Fiyat Listesi',
      description: 'Delay24, uçuş gecikmeleri ve iptalleri konusunda yolcu hakları tazminat sürecini dijital olarak yöneten bağımsız bir platformdur. Havayolları ile herhangi bir organik bağı yoktur.',
      rights: '© 2025 Delay24. Tüm hakları saklıdır.'
    },
    claimForm: {
      title: 'Tazminat Talebinizi Başlatın',
      subtitle: 'Uçuş bilgilerinizi girin ve hak sahibi olup olmadığınızı öğrenin',
      step1: 'Uçuş Bilgileri',
      step2: 'Yolcu Bilgileri',
      step3: 'İmza',
      step4: 'Belgeler & Onaylar',
      departureAirport: 'Kalkış Havalimanı',
      arrivalAirport: 'Varış Havalimanı',
      flightNumber: 'Uçuş Numarası',
      flightDate: 'Uçuş Tarihi',
      selectDate: 'Tarih seçin',
      flightDateFuture: 'Uçuş tarihi gelecekte olamaz.',
      flightDateTooOld: 'Uçuş tarihi son {years} yıl içinde olmalıdır.',
      flightDateInvalid: 'Geçerli bir uçuş tarihi girin.',
      flightSegmentLabel: 'Uçuş',
      addConnectingFlight: 'Bağlantılı uçuş ekle',
      removeFlight: 'Uçuşu kaldır',
      singleBookingQuestion: 'Tüm uçuşlar tek bir rezervasyonda mıydı?',
      singleBookingYes: 'Evet, tek rezervasyon',
      singleBookingNo: 'Hayır, ayrı rezervasyonlar',
      selfTransferStopTitle: 'Yalnızca tek bir rezervasyondaki uçuşlar için talep açabiliriz',
      selfTransferStopBody: 'Uçuşlarınız ayrı rezervasyonlarda alındığından, yalnızca aksaklık yaşanan tek rezervasyondaki uçuşlar için tazminat talebinde bulunabiliriz. Lütfen yukarıdaki listeden diğer rezervasyonlarınıza ait uçuşları kaldırın; yalnızca aksaklık yaşanan rezervasyondaki uçuşları bırakın ve devam edin.',
      segmentNotFoundTitle: 'Bu uçuşu bulamadık',
      segmentNotFoundBody: 'Girdiğiniz uçuş numarası ve tarihiyle eşleşen bir uçuş bulamadık. Lütfen bilgileri kontrol edin ve uçuş numarasını biniş kartınızda yazdığı şekilde girin.',
      segmentAmbiguousTitle: 'Bu uçuş numarası birden fazla uçuşla eşleşiyor',
      segmentAmbiguousBody: 'Girdiğiniz uçuş numarası birden fazla gerçek uçuşla eşleşti. Hangisi olduğunu netleştirmek için lütfen biniş kartınızdaki uçuş numarasını girin.',
      problemType: 'Sorun neydi?',
      delayed: 'Uçuşum gecikti',
      cancelled: 'Uçuşum iptal edildi',
      denied: 'Uçağa alınmadım (overbook)',
      missed: 'Bağlantı uçuşumu kaçırdım',
      delayLength: 'Nihai varış noktanıza ne kadar geç ulaştınız?',
      delayLengthNote: 'Tek bir uçuşun değil, yolculuğunuzun son durağına toplam varış gecikmenizi seçin. Bağlantınızı kaçırıp yeniden rezerve edildiyseniz, ilk uçuşun kalkış gecikmesini değil, nihai varış noktanıza gerçekte ne kadar geç ulaştığınızı baz alın.',
      delay0to2: '0–2 saat',
      delay2to3: '2–3 saat',
      delay3to4: '3–4 saat',
      delay4plus: '4+ saat',
      alreadyClaimed: 'Bu uçuş için daha önce tazminat talebinde bulundunuz mu?',
      alreadyClaimedYes: 'Evet',
      alreadyClaimedNo: 'Hayır',
      alreadyClaimedNoteLabel: 'Detaylar (isteğe bağlı)',
      alreadyClaimedNotePlaceholder: 'Örn: hangi şirket üzerinden, ne zaman başvurdunuz?',
      alreadyClaimedRequired: 'Lütfen bu uçuş için daha önce tazminat talebinde bulunup bulunmadığınızı belirtin.',
      firstName: 'Ad',
      lastName: 'Soyad',
      dateOfBirth: 'Doğum Tarihi',
      dateOfBirthFuture: 'Doğum tarihi gelecekte olamaz.',
      dateOfBirthInvalid: 'Geçerli bir doğum tarihi girin.',
      email: 'E-posta',
      phone: 'Telefon',
      phoneCountryCode: 'Ülke kodu',
      phoneInvalid: "Yalnızca rakam girin; başındaki 0'ı ve ülke kodunu yazmayın, ör. 5354508339.",
      phoneTooLong: '{code} kodlu bir telefon numarası en fazla {max} haneli olabilir.',
      airline: 'Havayolu',
      bookingRef: 'Rezervasyon / PNR Numarası',
      bookingRefHint: 'Havayolu onay e-postanızda veya biniş kartınızda yer alan rezervasyon kodu (genellikle 6 harf ve rakam).',
      bookingRefInvalid: 'Yalnızca harf ve rakam kullanın.',
      bookingRefTooLong: 'Rezervasyon kodu en fazla {max} karakter olabilir.',
      bookingRefNamed: '{ref} rezervasyonundaki',
      bookingRefFallback: 'bu rezervasyondaki',
      passengerListHeading: 'Bu rezervasyondaki yolcular',
      leadPassengerLabel: 'Ana Yolcu (Siz)',
      passengerLabel: 'Yolcu',
      addPassenger: 'Yolcu ekle',
      sameBookingHint: 'Yalnızca {booking} yolcuları ekleyin. Farklı bir rezervasyon kodu olan yolcular kendi taleplerini ayrıca oluşturmalıdır.',
      removePassenger: 'Yolcuyu kaldır',
      optionalLabel: '(isteğe bağlı)',
      underEighteenQuestion: 'Bu yolcu 18 yaşından küçük mü?',
      underEighteenYes: 'Evet',
      underEighteenNo: 'Hayır',
      leadNotAdult: 'Başvuruyu yapan kişi 18 yaşında veya daha büyük olmalıdır. Bir çocuk adına başvuruyorsanız, önce kendi bilgilerinizi girin ve çocuğu yolcu olarak ekleyin.',
      minorIsAdult: 'Bu doğum tarihine göre yolcu 18 yaşında veya daha büyük. Yukarıdaki sorunun yanıtı "Hayır" olmalı mı?',
      duplicateAdultEmail: 'Her yetişkinin kendi e-posta adresi olmalıdır.',
      coAdultSigningHint: '{name}, kendi bilgilerini eklemesi ve yetki belgesini imzalaması için e-postayla bir bağlantı alacak. Talebin bu yolcuya ait kısmı ancak imzaladıktan sonra takip edilebilir.',
      coAdultNameFallback: 'Bu yolcu',
      sameAddressAsLead: 'Ana yolcuyla aynı adreste yaşıyor',
      addressOfDomicile: 'İkamet adresi',
      addressOfDomicileHint: 'Yetki belgenizde görüneceği şekliyle',
      street: 'Açık adres',
      streetPlaceholder: 'Sokak, numara, daire',
      postalCode: 'Posta kodu',
      city: 'Şehir',
      addressCountry: 'Ülke',
      addressTooLong: 'En fazla {max} karakter olabilir.',
      signatureTitle: 'İmzanız',
      signatureDescription: 'Baş yolcu olarak, kendi yetkilendirmenizi ve sizinle seyahat eden reşit olmayan yolcuların yetkilendirmelerini yasal vasi sıfatıyla imzalıyorsunuz. Başvurudaki diğer yetişkin yolculara, kendi yetkilendirmelerini imzalamaları için e-posta gönderilecektir. Devam etmek için aşağıya imzanızı çizin.',
      signaturePlaceholder: 'Tıklayın ve imzanızı çizin',
      clearSignature: 'Temizle',
      signatureConsentPre: 'İmzamın ',
      signatureConsentLink: 'Yetki Belgesi',
      signatureConsentPost: ' üzerinde gösterilmesine izin veriyorum.',
      authorityDocumentMissingFields: 'Lütfen önce ad, soyad ve rezervasyon kodunu girin.',
      authorityDocumentDownloadFailed: 'Yetki belgesi indirilemedi. Lütfen tekrar deneyin.',
      signatureRequired: 'Devam etmek için lütfen imzanızı atın.',
      ticket: 'Uçak bileti',
      boardingPass: 'Boarding kartı',
      airlineDocs: 'Havayolu e-postası / belgeler',
      consentAll: 'Tümünü kabul ediyorum',
      termsAndConditionsConsentPre: '',
      termsAndConditionsConsentLink: 'Kullanım Koşulları',
      termsAndConditionsConsentPost: '\'nı okudum ve kabul ediyorum.',
      privacyPolicyConsentPre: '',
      privacyPolicyConsentLink: 'Gizlilik Politikası',
      privacyPolicyConsentPost: '\'nı okudum ve kabul ediyorum.',
      priceListConsentPre: '',
      priceListConsentLink: 'Fiyat Listesi',
      priceListConsentPost: '\'ni okudum ve kabul ediyorum.',
      next: 'İleri',
      back: 'Geri',
      submit: 'Başvurumu Gönder',
      checking: 'Kontrol ediliyor...',
      ineligibleTitle: 'Tazminat Mümkün Değil',
      ineligibleMessage: 'Maalesef bu uçuş için tazminat almak mümkün görünmüyor.',
      notPursuableTitle: 'Haklarınız Olabilir — Ancak Bu Talebi Henüz Üstlenemiyoruz',
      notPursuableMessage: 'Verdiğiniz bilgilere göre, AB hava yolcu hakları düzenlemeleri (EC 261) kapsamında tazminat hakkınız olabilir. Ne yazık ki delay24 şu anda bu tür talepleri üstlenememektedir. Kapsamımızı genişletmek için çalışıyoruz — lütfen daha sonra tekrar kontrol edin.',
      successTitle: 'Başvurunuz Alındı!',
      successMessage: 'Referans numaranız:',
      successInfo: 'Detaylar e-postanıza gönderildi. Ortalama 4-16 hafta içinde size dönüş yapacağız.',
      successCoTravellerInfo: 'Başvurunuzdaki diğer yetişkin yolculara, kendi yetkilendirmelerini imzalamaları için bir e-posta gönderilecektir.',
      backToHome: 'Ana Sayfaya Dön',
      fillAllFields: 'Lütfen tüm alanları doldurun.',
      invalidEmail: 'Lütfen geçerli bir e-posta adresi girin.',
      checkFailed: 'Uygunluk kontrolü başarısız oldu. Lütfen tekrar deneyin.',
      submitFailed: 'Başvurunuz gönderilemedi. Lütfen tekrar deneyin.',
      travelDocLabel: 'Seyahat Belgesi (biniş kartı, e-bilet vb.)',
      identityDocLabel: 'Kimlik Belgesi',
      clickToSelect: 'Dosya seçmek için tıklayın',
      removeFile: 'Dosyayı kaldır',
      docsNote: 'Not: Belgelerinizi başvuru sonrası e-posta ile de gönderebilirsiniz.',
      selectPlaceholder: 'Seçiniz',
      fieldRequired: 'Bu alan zorunludur.',
      stepConfirm: 'Uçuş Onayı',
      legLabel: '{n}. Uçuş',
      statusDelayed: 'Gecikmiş',
      statusCanceled: 'İptal Edildi',
      totalDelay: 'Toplam Gecikme',
      flightDistanceLabel: 'Uçuş Mesafesi',
      scheduledArrival: 'Planlanan Varış Saati',
      actualArrival: 'Gerçekleşen Varış Saati',
      notArrived: 'Uçmadı',
      estimateLabel: 'Tahmini Tazminat',
      perPerson: 'kişi başı',
      estimateNote: 'Ödeme tarihinde TCMB döviz satış kuru üzerinden TL karşılığı olarak ödenir.',
      isInfoCorrect: 'Bu bilgiler doğru mu?',
      confirmYes: 'Evet, bu benim uçuşum',
      confirmNo: 'Hayır, bu benim uçuşum değil',
      confirmRequired: 'Lütfen bir seçenek seçin.',
      hours: 'sa',
      minutes: 'dk',
      fileNotAdded: '{name} eklenmedi.',
      fileTooLarge: "Dosya boyutu 10 MB'ı aşamaz.",
      invalidFileType: 'Yalnızca PDF, PNG veya JPEG dosyaları kabul edilir.',
    },
    home: {
      heroTitle1: 'Uçuşunuz Gecikti mi?',
      heroTitle2Pre: '',
      heroTitle2Highlight: '250€ - 600€',
      heroTitle2Post: ' Tazminat Alın',
      heroSubtitle: '2 dakikada başvur, süreci bize bırak. Başarı garantisi - kazanamazsak ücret yok.',
      flightInputPlaceholder: 'Uçuş numaranızı girin (örn: TK1234)',
      checkButton: 'Kontrol Et',
      quickCheckTerms: '✓ Ücretsiz kontrol  •  ✓ 2 dakika  •  ✓ Kazanamazsak ücret yok',
      whySubtitle: 'Binlerce yolcunun güvendiği platform',
      ctaTitle: 'Tazminatınızı Talep Etmeye Hazır mısınız?',
      ctaSubtitle: 'Hemen başlayın, tüm süreci biz halledelim.',
      ctaBullet1: '2 dakikada başvuru',
      ctaBullet2: 'Kazanamazsak ücret yok',
    },
    faq: {
      title: 'Sıkça Sorulan Sorular',
      subtitle: 'Tazminat süreci hakkında merak ettikleriniz',
      contactTitle: 'Sorunuz mu var?',
      contactText: 'Ekibimiz size yardımcı olmaktan mutluluk duyar.',
      contactLink: 'İletişime Geçin →',
      items: [
        {
          question: 'Ücreti ne zaman ödüyorum?',
          answer: 'Sadece tazminat kazandığımızda, havayolundan gelen tutar üzerinden komisyon alıyoruz. Kazanamazsak hiçbir ücret ödemezsin. İlk başvuru sırasında herhangi bir ödeme talep etmiyoruz.'
        },
        {
          question: 'Ne kadar tazminat alabilirim?',
          answer: 'AB uçuşlarında tazminat tutarı genellikle 250€ ile 600€ arasında değişir. Tutar uçuş mesafesine, gecikme süresine ve hangi mevzuatın geçerli olduğuna bağlıdır. Sistemimiz başvuru sırasında size bir tahmin gösterecektir.'
        },
        {
          question: 'Süreç ne kadar sürüyor?',
          answer: 'Havayolunun yanıt süresine göre değişir, ancak ortalama 4-16 hafta içinde sonuçlanır. Eğer havayolu ilk başvuruyu reddederse ve dava açmamız gerekirse, süre uzayabilir. Sürecin her adımında sizi bilgilendiririz.'
        },
        {
          question: 'Hangi durumlarda tazminat hakkım olmaz?',
          answer: 'Olağanüstü durumlar (ağır hava koşulları, havaalanı grevleri, güvenlik riskleri, savaş durumu) havayolunun kontrolü dışındaysa tazminat hakkı doğmayabilir. Her vakayı ayrı ayrı inceliyoruz ve size net bilgi veriyoruz.'
        },
        {
          question: 'Eski uçuşlar için başvuru yapabilir miyim?',
          answer: 'Evet! AB uçuşları için genellikle 3 yıllık, Türkiye uçuşları için 2 yıllık zamanaşımı süresi vardır. Bu süre içindeki uçuşlar için başvuru yapabilirsiniz.'
        },
        {
          question: 'Havayolu bana voucher teklif etti, kabul etmeli miyim?',
          answer: 'Voucher (kupon) yerine nakdi tazminat almayı tercih edebilirsiniz. Voucher kabul ederseniz, genellikle nakdi tazminat hakkınızdan feragat etmiş olursunuz. Bize başvurmadan önce herhangi bir voucher kabul etmemenizi öneririz.'
        },
        {
          question: 'Aktarmalı uçuşlarda tazminat nasıl hesaplanır?',
          answer: 'Aktarmalı uçuşlarda, nihai varış noktanıza ulaşma süreniz baz alınır. Eğer ilk uçuş gecikmesi nedeniyle bağlantı uçuşunu kaçırdıysanız ve nihai varışta 3 saatten fazla gecikme olduysa tazminat hakkınız doğar.'
        },
        {
          question: 'Belgelerimi nasıl paylaşmam gerekiyor?',
          answer: 'Başvuru formunda belgeleri yükleyebilir ya da daha sonra e-posta ile gönderebilirsiniz. İhtiyaç duyduğumuz belgeler: uçak bileti, boarding kartı ve havayoluyla yaptığınız yazışmalar (varsa).'
        },
        {
          question: 'Hangi ülkelerde geçerlisiniz?',
          answer: 'EU261 kapsamındaki tüm AB uçuşları (AB\'den kalkan, AB\'ye varan veya AB havayolu ile yapılan uçuşlar) ve Türkiye SHY (Sivil Havacılık Yetkilisi) mevzuatına tabi uçuşlar için hizmet veriyoruz.'
        },
        {
          question: 'Havayolu ile kendim görüştüm ve reddedildi, yine de başvurabilir miyim?',
          answer: 'Evet! Havayolları ilk başvurularda çoğu zaman ret cevabı verir. Biz hukuki süreci devam ettirip, gerekirse dava açarak hakkınızı almanız için mücadele ediyoruz. Sizin adınıza profesyonel bir ekip harekete geçer.'
        }
      ]
    },
    contact: {
      title: 'İletişim',
      subtitle: 'Sorularınız mı var? Size yardımcı olmaktan mutluluk duyarız.',
      emailLabel: 'E-posta',
      addressLabel: 'Adres',
      formTitle: 'Bize Ulaşın',
      nameField: 'Ad Soyad',
      emailField: 'E-posta',
      subjectField: 'Konu',
      messageField: 'Mesajınız',
      sendButton: 'Mesaj Gönder',
      sending: 'Gönderiliyor…',
      successToast: 'Mesajınız gönderildi! 2 iş günü içinde e-posta adresinize dönüş yapacağız.',
      errorToast:
        'Mesajınız gönderilemedi. Lütfen daha sonra tekrar deneyin veya doğrudan support@delay24.com adresine e-posta gönderin.',
    },
    autocomplete: {
      searching: 'Aranıyor…',
      error: 'Öneriler yüklenemedi.',
      noMatches: 'Eşleşme bulunamadı',
      select: 'Seçiniz',
    },
    notFound: {
      heading: 'Sayfa Bulunamadı',
      body: 'Aradığınız sayfa mevcut değil veya taşınmış olabilir.',
      button: 'Ana Sayfaya Dön',
    },
    signingPage: {
      loading: 'Bağlantınız kontrol ediliyor…',
      title: 'Tazminat Başvurunuz',
      greeting: 'Merhaba {name},',
      addedBy: '{lead} sizi tazminat başvurusuna ekledi.',
      bookingRef: 'Rezervasyon / PNR Numarası',
      wrongBooking: 'Bu sizin rezervasyon kodunuz değil mi? Önce rezervasyon onayınızı kontrol edin. Kodunuz farklıysa burada imzalamayın, {link}',
      wrongBookingLink: 'kendi talebinizi oluşturun.',
      flightsTitle: 'Uçuş Bilgileri',
      legLabel: '{n}. Uçuş',
      airline: 'Havayolu',
      flightNumber: 'Uçuş Numarası',
      flightDate: 'Uçuş Tarihi',
      notFoundTitle: 'Bu bağlantı geçerli değil',
      notFoundMessage: 'Bağlantıyı e-postanızdan eksiksiz açtığınızdan emin olun.',
      notFoundNewestEmail: 'Yeni bir bağlantı istediyseniz, en son gelen e-postadaki bağlantıyı kullanın; önceki bağlantılar artık çalışmaz.',
      usedTitle: 'Bu bağlantı zaten kullanıldı',
      usedMessage: 'Yetkilendirmeniz bu bağlantıyla zaten imzalandı. Yapmanız gereken başka bir şey yok.',
      expiredTitle: 'Bu bağlantının süresi doldu',
      expiredMessage: 'İmza bağlantıları, başvurunuzun güvenliği için yalnızca sınırlı bir süre geçerlidir.',
      resend: 'Bana yeni bir bağlantı gönder',
      resending: 'Gönderiliyor…',
      resendFailed: 'Yeni bağlantı gönderilemedi. Lütfen tekrar deneyin.',
      resentTitle: 'Yeni bağlantınız gönderildi',
      resentMessage: 'Size yeni bir imza bağlantısı e-postayla gönderdik. Lütfen o e-postadaki bağlantıyı kullanın; bu bağlantı artık çalışmaz.',
      errorTitle: 'Sayfa yüklenemedi',
      errorMessage: 'Bir sorun oluştu. Lütfen tekrar deneyin.',
      retry: 'Tekrar Dene',
      detailsTitle: 'Bilgileriniz',
      detailsHint: '{lead} yalnızca adınızı ve e-posta adresinizi paylaştı. Yetkilendirmenizi imzalamak için kalan bilgilerinizi ekleyin.',
      documentsTitle: 'Belgeleriniz',
      documentsHint: 'İsterseniz seyahat ve kimlik belgenizi şimdi ekleyin; eklemeden de imzalayabilirsiniz.',
      signatureDescription: 'Kendi yetkilendirmenizi imzalıyorsunuz. Devam etmek için aşağıya imzanızı çizin.',
      previewNeedsAddress: 'Yetki belgenizin önizlemesini indirmek için önce ikamet adresinizi doldurun.',
      sign: 'İmzala ve Gönder',
      signing: 'Gönderiliyor…',
      signFailed: 'İmzanız gönderilemedi. Lütfen tekrar deneyin.',
      signedTitle: 'Yetkilendirmeniz alındı',
      signedMessage: 'Teşekkürler. Yapmanız gereken başka bir şey yok; başvurunuzu buradan sonra biz takip edeceğiz.',
      underAgeTitle: 'Yetki belgenizi kendiniz imzalayamazsınız',
      underAgeMessage:
        '18 yaşından küçük bir yolcu kendi yetki belgesini imzalayamaz; belgenin ebeveyni veya yasal vasisi tarafından imzalanması gerekir. Delay24, bunu çözmek için {lead} ile iletişime geçecek.',
      underAgeCorrect: 'Doğum tarihinizi yanlış girdiyseniz, yukarıdan düzeltebilirsiniz.',
    },
  },
  en: {
    nav: {
      home: 'Home',
      howItWorks: 'How It Works',
      claim: 'Claim Now',
      faq: 'FAQ',
      about: 'About',
      contact: 'Contact'
    },
    hero: {
      title: 'If Your Flight Was Delayed, You May Be Owed Compensation.',
      subtitle: 'Delay24 files flight delay, cancellation and overbooking claims on your behalf, based on EU261 and national regulations. You don\'t deal with paperwork – we handle everything. No win, no fee.',
      ctaPrimary: 'Check Your Compensation Now',
      ctaSecondary: 'See How It Works',
      trust1: 'Free eligibility check',
      trust2: 'No win, no fee',
      trust3: 'EU & Turkey regulations compliant'
    },
    howItWorks: {
      title: 'Your Claim in 3 Simple Steps',
      step1Title: '1. Enter Your Flight Details',
      step1Text: 'Tell us your departure and arrival airports, flight number and travel date. Our system checks your eligibility under EU261 and other applicable regulations.',
      step2Title: '2. We Handle the Airline',
      step2Text: 'We collect the required documents and file an official claim with the airline. When needed, our partner lawyers pursue the case on your behalf.',
      step3Title: '3. Payout and Fair Commission',
      step3Text: 'Once the airline pays the compensation, we deduct our agreed commission and transfer the rest to you. If we don\'t win, you don\'t pay anything.'
    },
    whyDelay24: {
      title: 'Why Choose Delay24?',
      reason1Title: 'Zero Upfront Cost',
      reason1Text: 'You pay nothing to submit a claim. We cover the costs and only charge a commission if we win.',
      reason2Title: 'Professional Airline Negotiation',
      reason2Text: 'Airlines often reject claims at the first attempt. We keep pushing with the right legal arguments until you get what you\'re entitled to.',
      reason3Title: 'Focused on Europe & Turkey',
      reason3Text: 'We handle claims for EU-related flights under EU261 as well as flights linked to Turkey under SHY.',
      reason4Title: 'Transparent Digital Tracking',
      reason4Text: 'Monitor your case status in your online dashboard. See which step we took and when – fully transparent.'
    },
    eligibility: {
      title: 'You May Be Eligible If…',
      bullet1: 'Your flight was delayed by more than 3 hours',
      bullet2: 'Your flight was cancelled',
      bullet3: 'You were denied boarding due to overbooking',
      bullet4: 'You missed a connecting flight because of a previous delay',
      bullet5: 'Your journey is linked to the EU (departing from, arriving to, or operated by an EU carrier)',
      note: 'Extraordinary circumstances such as severe weather, airport/ATC strikes or serious security risks may exclude compensation. We assess each case individually.'
    },
    miniFaq: {
      title: 'Quick FAQ',
      q1: 'When do I have to pay?',
      a1: 'Only if we successfully obtain compensation from the airline. Our fee is a commission on the payout – no win, no fee.',
      q2: 'How much compensation can I get?',
      a2: 'Under EU261, amounts typically range between €250 and €600, depending on flight distance, delay length and itinerary specifics.',
      q3: 'How long does it take?',
      a3: 'It depends on how fast the airline responds, but most cases are resolved within 4–16 weeks. If legal proceedings are required, it can take longer.'
    },
    footer: {
      menu: 'Menu',
      legal: 'Legal',
      terms: 'Terms of Use',
      privacy: 'Privacy Policy',
      kvkk: 'Data Protection',
      priceList: 'Price List',
      description: 'Delay24 is an independent digital platform that manages flight delay and cancellation compensation claims for passengers. We are not affiliated with any airline.',
      rights: '© 2025 Delay24. All rights reserved.'
    },
    claimForm: {
      title: 'Start Your Claim',
      subtitle: 'Enter your flight details and find out if you\'re eligible',
      step1: 'Flight Details',
      step2: 'Passenger Details',
      step3: 'Signature',
      step4: 'Documents & Consent',
      departureAirport: 'Departure Airport',
      arrivalAirport: 'Arrival Airport',
      flightNumber: 'Flight Number',
      flightDate: 'Flight Date',
      selectDate: 'Select date',
      flightDateFuture: 'The flight date cannot be in the future.',
      flightDateTooOld: 'The flight date must be within the last {years} years.',
      flightDateInvalid: 'Enter a valid flight date.',
      flightSegmentLabel: 'Flight',
      addConnectingFlight: 'Add connecting flight',
      removeFlight: 'Remove flight',
      singleBookingQuestion: 'Were all flights on a single booking?',
      singleBookingYes: 'Yes, one booking',
      singleBookingNo: 'No, separate bookings',
      selfTransferStopTitle: 'We can only claim flights from a single booking',
      selfTransferStopBody: 'Because your flights were booked separately, we can only pursue compensation for the flights on the single booking that was disrupted. Please remove the flights that belong to your other bookings from the list above, keeping only the flights from the disrupted booking, then continue.',
      segmentNotFoundTitle: "We couldn't find this flight",
      segmentNotFoundBody: "We couldn't match the flight number and date you entered to a real flight. Please check the details and enter the flight number exactly as it appears on your boarding pass.",
      segmentAmbiguousTitle: 'This flight number matches more than one flight',
      segmentAmbiguousBody: 'The flight number you entered matched more than one real flight. Please enter the flight number from your boarding pass so we can pinpoint the right one.',
      problemType: 'What was the problem?',
      delayed: 'My flight was delayed',
      cancelled: 'My flight was cancelled',
      denied: 'I was denied boarding (overbooked)',
      missed: 'I missed my connection',
      delayLength: 'How late did you arrive at your final destination?',
      delayLengthNote: 'Choose your total arrival delay at the last stop of your journey — not the delay of any single flight. If you missed a connection and were rebooked, base this on how late you actually reached your final destination, not the first flight\'s departure delay.',
      delay0to2: '0–2 hours',
      delay2to3: '2–3 hours',
      delay3to4: '3–4 hours',
      delay4plus: '4+ hours',
      alreadyClaimed: 'Have you already claimed compensation for this flight?',
      alreadyClaimedYes: 'Yes',
      alreadyClaimedNo: 'No',
      alreadyClaimedNoteLabel: 'Details (optional)',
      alreadyClaimedNotePlaceholder: 'E.g. which company you claimed through, and when',
      alreadyClaimedRequired: 'Please tell us whether you have already claimed compensation for this flight.',
      firstName: 'First Name',
      lastName: 'Last Name',
      dateOfBirth: 'Date of Birth',
      dateOfBirthFuture: 'Date of birth cannot be in the future.',
      dateOfBirthInvalid: 'Enter a valid date of birth.',
      email: 'Email',
      phone: 'Phone',
      phoneCountryCode: 'Country code',
      phoneInvalid: 'Enter digits only, without the leading 0 or the country code, e.g. 5354508339.',
      phoneTooLong: 'A phone number with {code} can have at most {max} digits.',
      airline: 'Airline',
      bookingRef: 'Booking / PNR Reference',
      bookingRefHint: "The booking code from your airline's confirmation email or boarding pass (usually 6 letters and numbers).",
      bookingRefInvalid: 'Use letters and numbers only.',
      bookingRefTooLong: 'A booking reference can have at most {max} characters.',
      bookingRefNamed: 'booking {ref}',
      bookingRefFallback: 'this booking',
      passengerListHeading: 'Who was on this booking',
      leadPassengerLabel: 'Lead Passenger (You)',
      passengerLabel: 'Passenger',
      addPassenger: 'Add passenger',
      sameBookingHint: 'Only add passengers on {booking}. Anyone with a different booking reference needs to file their own claim.',
      removePassenger: 'Remove passenger',
      optionalLabel: '(optional)',
      underEighteenQuestion: 'Is this passenger under 18?',
      underEighteenYes: 'Yes',
      underEighteenNo: 'No',
      leadNotAdult: "The person filing must be 18 or over. If you're filing for a child, enter your own details first and add the child as a passenger.",
      minorIsAdult: 'This date of birth makes the passenger 18 or over. Should the answer above be "No"?',
      duplicateAdultEmail: 'Each adult needs their own email address.',
      coAdultSigningHint: "We'll email {name} a link to add their own details and sign their authority document. Their share of the claim can only be pursued once they sign.",
      coAdultNameFallback: 'this passenger',
      sameAddressAsLead: 'Lives at the same address as the lead passenger',
      addressOfDomicile: 'Address of domicile',
      addressOfDomicileHint: 'As it will appear on your authority document',
      street: 'Street address',
      streetPlaceholder: 'Street, number, apartment',
      postalCode: 'Postal code',
      city: 'City',
      addressCountry: 'Country',
      addressTooLong: 'Must be at most {max} characters.',
      signatureTitle: 'Your Signature',
      signatureDescription: 'As the lead passenger you sign your own authorization and, as legal guardian, the authorization for any minors travelling with you. Any other adults on the claim will receive an email to sign their own authorization. Draw your signature below to continue.',
      signaturePlaceholder: 'Click and draw your signature',
      clearSignature: 'Clear',
      signatureConsentPre: 'I authorize my signature to be shown on the ',
      signatureConsentLink: 'Authority Document',
      signatureConsentPost: '.',
      authorityDocumentMissingFields: 'Please enter your name and booking reference first.',
      authorityDocumentDownloadFailed: 'Could not download the authority document. Please try again.',
      signatureRequired: 'Please sign before proceeding.',
      ticket: 'Flight ticket',
      boardingPass: 'Boarding pass',
      airlineDocs: 'Airline email / documents',
      consentAll: 'I agree to all',
      termsAndConditionsConsentPre: 'I have read and accept the ',
      termsAndConditionsConsentLink: 'Terms and Conditions',
      termsAndConditionsConsentPost: '.',
      privacyPolicyConsentPre: 'I have read and acknowledge the ',
      privacyPolicyConsentLink: 'Privacy Policy',
      privacyPolicyConsentPost: '.',
      priceListConsentPre: 'I have read and acknowledge the ',
      priceListConsentLink: 'Price List',
      priceListConsentPost: '.',
      next: 'Next',
      back: 'Back',
      submit: 'Submit My Claim',
      checking: 'Checking...',
      ineligibleTitle: 'Compensation Not Possible',
      ineligibleMessage: 'Unfortunately, compensation is not possible for this flight.',
      notPursuableTitle: 'You Likely Have Rights — But We Can\'t Take This Case Yet',
      notPursuableMessage: 'Based on what you\'ve told us, you probably have compensation rights under EU air passenger regulations (EC 261). Unfortunately, delay24 can\'t pursue this type of claim at the moment. We\'re working to expand our coverage — please check back later.',
      successTitle: 'Your Claim Has Been Received!',
      successMessage: 'Your reference number:',
      successInfo: 'Details have been sent to your email. We will get back to you within 4-16 weeks on average.',
      successCoTravellerInfo: 'Any co-travelling adults on your claim will receive an email to sign their own authorization.',
      backToHome: 'Back to Home',
      fillAllFields: 'Please fill in all fields.',
      invalidEmail: 'Please enter a valid email address.',
      checkFailed: 'Eligibility check failed. Please try again.',
      submitFailed: 'Failed to submit your claim. Please try again.',
      travelDocLabel: 'Travel Document (boarding pass, e-ticket, etc.)',
      identityDocLabel: 'Identity Document',
      clickToSelect: 'Click to select a file',
      removeFile: 'Remove file',
      docsNote: 'Note: You can also send your documents via email after submission.',
      selectPlaceholder: 'Select',
      fieldRequired: 'This field is required.',
      stepConfirm: 'Flight Confirmation',
      legLabel: 'Flight {n}',
      statusDelayed: 'Delayed',
      statusCanceled: 'Cancelled',
      totalDelay: 'Total delay',
      flightDistanceLabel: 'Flight distance',
      scheduledArrival: 'Scheduled arrival time',
      actualArrival: 'Actual arrival time',
      notArrived: 'Not arrived',
      estimateLabel: 'Estimated compensation',
      perPerson: 'per person',
      estimateNote: 'Paid as the TL equivalent at the TCMB selling rate on the payment date.',
      isInfoCorrect: 'Is this information correct?',
      confirmYes: 'Yes, this is my flight',
      confirmNo: 'No, this is not my flight',
      confirmRequired: 'Please select an option.',
      hours: 'h',
      minutes: 'min',
      fileNotAdded: "{name} wasn't added.",
      fileTooLarge: 'File size cannot exceed 10 MB.',
      invalidFileType: 'Only PDF, PNG, or JPEG files are accepted.',
    },
    home: {
      heroTitle1: 'Flight Delayed or Cancelled?',
      heroTitle2Pre: 'Get up to ',
      heroTitle2Highlight: '€600',
      heroTitle2Post: '',
      heroSubtitle: 'Claim in 2 minutes, we handle everything. No win, no fee - guaranteed.',
      flightInputPlaceholder: 'Enter flight number (e.g., TK1234)',
      checkButton: 'Check Now',
      quickCheckTerms: '✓ Free check  •  ✓ 2 minutes  •  ✓ No win, no fee',
      whySubtitle: 'The platform trusted by thousands of passengers',
      ctaTitle: 'Ready to Claim Your Compensation?',
      ctaSubtitle: 'Start now and let us handle everything.',
      ctaBullet1: 'Apply in 2 minutes',
      ctaBullet2: 'No win, no fee',
    },
    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Everything you need to know about the compensation process',
      contactTitle: 'Still have questions?',
      contactText: 'Our team is happy to help you.',
      contactLink: 'Contact Us →',
      items: [
        {
          question: 'When do I have to pay?',
          answer: 'Only if we successfully obtain compensation from the airline. Our fee is a commission on the payout – no win, no fee. We do not request any upfront payment during the initial application.'
        },
        {
          question: 'How much compensation can I get?',
          answer: 'Under EU261, amounts typically range between €250 and €600. The amount depends on flight distance, delay length, and which regulations apply. Our system will show you an estimate during the application process.'
        },
        {
          question: 'How long does it take?',
          answer: 'It depends on how fast the airline responds, but most cases are resolved within 4-16 weeks. If the airline rejects the initial claim and we need to pursue legal action, it may take longer. We keep you informed at every step.'
        },
        {
          question: 'When am I not entitled to compensation?',
          answer: 'Extraordinary circumstances (severe weather, airport strikes, security risks, war) outside the airline\'s control may exclude compensation. We assess each case individually and provide you with clear information.'
        },
        {
          question: 'Can I claim for old flights?',
          answer: 'Yes! There is typically a 3-year statute of limitations for EU flights and 2 years for Turkey flights. You can claim for flights within these time frames.'
        },
        {
          question: 'The airline offered me a voucher, should I accept?',
          answer: 'You can choose to receive cash compensation instead of a voucher. If you accept a voucher, you typically waive your right to cash compensation. We recommend not accepting any voucher before contacting us.'
        },
        {
          question: 'How is compensation calculated for connecting flights?',
          answer: 'For connecting flights, the arrival time at your final destination is used. If you missed your connection due to a delay on the first flight and arrived at your final destination more than 3 hours late, you may be entitled to compensation.'
        },
        {
          question: 'How do I share my documents?',
          answer: 'You can upload documents in the application form or send them via email later. Documents we need: flight ticket, boarding pass, and any correspondence with the airline (if available).'
        },
        {
          question: 'Which countries do you cover?',
          answer: 'We serve all EU flights covered by EU261 (flights departing from, arriving to, or operated by an EU carrier) and flights subject to Turkey SHY (Civil Aviation Authority) regulations.'
        },
        {
          question: 'I contacted the airline myself and was rejected, can I still apply?',
          answer: 'Yes! Airlines often reject initial claims. We continue the legal process and, if necessary, file a lawsuit to get you what you\'re entitled to. A professional team acts on your behalf.'
        }
      ]
    },
    contact: {
      title: 'Contact Us',
      subtitle: "Have questions? We'd love to help you.",
      emailLabel: 'Email',
      addressLabel: 'Address',
      formTitle: 'Send Us a Message',
      nameField: 'Full Name',
      emailField: 'Email',
      subjectField: 'Subject',
      messageField: 'Your Message',
      sendButton: 'Send Message',
      sending: 'Sending…',
      successToast: "Your message has been sent! We'll reply to your email within 2 business days.",
      errorToast: "Your message couldn't be sent. Please try again later or email us directly at support@delay24.com.",
    },
    autocomplete: {
      searching: 'Searching…',
      error: 'Could not load suggestions.',
      noMatches: 'No matches',
      select: 'Select',
    },
    notFound: {
      heading: 'Page Not Found',
      body: 'The page you are looking for does not exist or may have moved.',
      button: 'Back to Home',
    },
    signingPage: {
      loading: 'Checking your link…',
      title: 'Your Compensation Claim',
      greeting: 'Hello {name},',
      addedBy: '{lead} added you to their compensation claim.',
      bookingRef: 'Booking / PNR Reference',
      wrongBooking: "Is this not your booking reference? Check your booking confirmation first. If yours is different, please don't sign here. {link}",
      wrongBookingLink: 'File your own claim instead.',
      flightsTitle: 'Flight Details',
      legLabel: 'Flight {n}',
      airline: 'Airline',
      flightNumber: 'Flight Number',
      flightDate: 'Flight Date',
      notFoundTitle: "This link isn't valid",
      notFoundMessage: 'Make sure you opened the full link from your email.',
      notFoundNewestEmail: 'If you asked for a new link, use the one in the newest email; earlier links no longer work.',
      usedTitle: 'This link has already been used',
      usedMessage: "Your authorization has already been signed with this link, so there's nothing left for you to do.",
      expiredTitle: 'This link has expired',
      expiredMessage: 'Signing links are only valid for a limited time, to keep your claim secure.',
      resend: 'Send me a new link',
      resending: 'Sending…',
      resendFailed: "We couldn't send a new link. Please try again.",
      resentTitle: 'Your new link has been sent',
      resentMessage: "We've emailed you a new signing link. Please use the link in that email; this one no longer works.",
      errorTitle: "We couldn't load this page",
      errorMessage: 'Something went wrong. Please try again.',
      retry: 'Try Again',
      detailsTitle: 'Your Details',
      detailsHint: '{lead} only shared your name and email. Add the rest of your details to sign your authorization.',
      documentsTitle: 'Your Documents',
      documentsHint: 'Add your travel and identity documents now if you like; you can sign without them.',
      signatureDescription: 'You are signing your own authorization. Draw your signature below to continue.',
      previewNeedsAddress: 'Fill in your Address of domicile first to download a preview of your authority document.',
      sign: 'Sign and Submit',
      signing: 'Sending…',
      signFailed: "Your signature couldn't be sent. Please try again.",
      signedTitle: 'Your authorization is in',
      signedMessage: "Thank you. There's nothing else you need to do; we'll take your claim from here.",
      underAgeTitle: "You can't sign your own authority document",
      underAgeMessage:
        "A passenger under 18 can't sign their own authority document; it has to be signed by their parent or legal guardian. Delay24 will contact {lead} to sort it out.",
      underAgeCorrect: 'If you entered your date of birth wrong, you can correct it above.',
    },
  }
};
